import { ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ChevronDown,
  Home,
  LayoutDashboard,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  ShieldCheck,
  SquareArrowOutUpRight,
  X,
} from "lucide-react";
import { AdminMenuItem as ApiMenuItem, AdminUser, fetchMyMenu } from "./api";
import { resolveAdminMenuIcon } from "./adminMenuIcons";
import { AdminBreadcrumb } from "./components/AdminBreadcrumb";
import { defaultLogoUrl } from "@/utils/branding";

type AdminLayoutProps = {
  children: ReactNode;
  title: string;
  user: AdminUser | null;
  onLogout: () => void;
  sectionLabel?: string;
};

const sidebarStorageKey = "fulling_admin_sidebar_collapsed";

/** 側邊選單實際渲染用的連結項目，是把 API 回傳的原始選單資料整理過後的顯示用資料。 */
type NavLinkItem = {
  key: string;
  label: string;
  href: string;
  active: boolean;
  external: boolean;
  icon?: ReactNode;
};

function BrandLogo({ collapsed }: { collapsed: boolean }) {
  if (collapsed) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-white p-0">
        <img className="h-full w-full object-contain" src={defaultLogoUrl} alt="富聆聽健康" />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
      <div className="flex h-[58px] w-[58px] shrink-0 items-center justify-center rounded-md p-0">
        <img className="h-full w-full object-contain" src={defaultLogoUrl} alt="富聆聽健康" />
      </div>
      <div className="shrink-0 whitespace-nowrap text-[22px] font-extrabold leading-none tracking-normal text-[#315746]">富聆聽<span className="text-[#d4a574]">健康</span></div>
    </div>
  );
}

function NavLink({ item, onNavigate, indented, collapsed }: { item: NavLinkItem; onNavigate?: () => void; indented?: boolean; collapsed?: boolean }) {
  const className = `flex min-h-11 items-center rounded-md px-3 py-2 text-sm font-bold transition ${collapsed ? "justify-center" : ""} ${indented ? "ml-4" : ""} ${
    item.active ? "bg-[#e9f4ec] text-[#245438]" : "text-[#59665f] hover:bg-[#f0f5f2] hover:text-[#315746]"
  }`;

  return (
    <a
      className={className}
      href={item.href}
      key={item.key}
      onClick={onNavigate}
      rel={item.external ? "noopener noreferrer" : undefined}
      target={item.external ? "_blank" : undefined}
      title={item.label}
    >
      {item.icon && <span className="shrink-0">{item.icon}</span>}
      {!collapsed && <span className="ml-2 flex items-center gap-1.5">
        {item.label}
        {item.external && <SquareArrowOutUpRight className="text-[#9A8A7A]" size={12} />}
      </span>}
    </a>
  );
}

function AdminMenuGroup({
  collapsed,
  groupLabel,
  icon,
  items,
  expanded,
  onToggle,
  onNavigate,
}: {
  collapsed: boolean;
  groupLabel: string;
  icon: ReactNode;
  items: NavLinkItem[];
  expanded: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
}) {
  const groupActive = items.some((item) => item.active);
  const activeClass = groupActive ? "bg-[#e9f4ec] text-[#245438]" : "text-[#59665f] hover:bg-[#f0f5f2] hover:text-[#315746]";

  if (collapsed) {
    return (
      <div
        className="group relative"
      >
        <button
          aria-label={groupLabel}
          className={`flex h-12 w-full items-center justify-center rounded-md transition ${activeClass}`}
          type="button"
        >
          {icon}
        </button>

        <div className="absolute left-[44px] top-0 z-[90] hidden h-12 w-5 group-hover:block group-focus-within:block" />

        <div
          className="pointer-events-none fixed bottom-3 left-[70px] top-3 z-[100] flex w-[286px] -translate-x-1 opacity-0 transition group-hover:pointer-events-auto group-hover:translate-x-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-x-0 group-focus-within:opacity-100"
        >
          <div className="flex min-h-0 w-full flex-col overflow-hidden rounded-md border border-[#dfe6e1] bg-white shadow-[0_16px_42px_rgba(32,61,50,.14)]">
            <div className="flex items-center gap-2 border-b border-[#edf0ee] px-4 py-3 text-sm font-bold text-[#315746]">
              {icon}
              {groupLabel}
          </div>
            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-3">
              {items.map((item) => (
                <NavLink item={item} key={item.key} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <button
        aria-expanded={expanded}
        className={`flex h-10 w-full items-center justify-between rounded-md px-3 text-sm font-bold transition ${
          groupActive ? "bg-[#e9f4ec] text-[#245438]" : "text-[#59665f] hover:bg-[#f0f5f2] hover:text-[#315746]"
        }`}
        onClick={onToggle}
        type="button"
      >
        <span className="inline-flex items-center gap-2">
          {icon}
          {groupLabel}
        </span>
        <ChevronDown className={`transition-transform ${expanded ? "rotate-180" : ""}`} size={16} />
      </button>

      {expanded && (
        <div className="space-y-1">
          {items.map((item) => (
            <NavLink indented item={item} key={item.key} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminLayout({ children, title, user, onLogout, sectionLabel = "預約記錄管理" }: AdminLayoutProps) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(sidebarStorageKey) === "1");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuItems, setMenuItems] = useState<ApiMenuItem[]>([]);

  useEffect(() => {
    localStorage.setItem(sidebarStorageKey, collapsed ? "1" : "0");
  }, [collapsed]);

  // 選單本身已經改成後台資料庫驅動（AdminMenuItem，包含「選單維護」自己這個項目），這裡登入後抓一次
  // 「我看得到的選單」，已經依照目前帳號的頁面權限標好 IsAllowed，不需要前端自己重算權限或特判
  // 超級管理者。抓取失敗（例如尚未登入）就顯示空選單，各頁面本來就會各自呼叫 getCurrentAdmin()
  // 驗證登入狀態並導回登入頁，這裡不用重複處理。
  useEffect(() => {
    let alive = true;
    fetchMyMenu()
      .then((data) => {
        if (!alive) return;
        setMenuItems(data.Items);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const desktopContentPadding = collapsed ? "lg:pl-[76px]" : "lg:pl-[320px]";
  const sidebarCollapsed = collapsed && !mobileMenuOpen;
  const currentPath = location.pathname;

  function isActivePath(path: string): boolean {
    return currentPath === path || currentPath.startsWith(`${path}/`);
  }

  function toNavItem(item: ApiMenuItem): NavLinkItem {
    return {
      key: item.AdminMenuItemId,
      label: item.Label,
      href: item.Path || "#",
      active: item.ItemType === "Page" && isActivePath(item.Path || "\0"),
      external: item.ItemType === "External",
      icon: resolveAdminMenuIcon(item.Icon),
    };
  }

  const visibleItems = menuItems.filter((item) => item.IsAllowed !== false);
  const topLevel = [...visibleItems].filter((item) => item.ParentId === null).sort((a, b) => a.SortOrder - b.SortOrder);

  const menuGroups = topLevel
    .filter((item) => item.ItemType === "Group")
    .map((group) => ({
      key: group.AdminMenuItemId,
      label: group.Label,
      icon: resolveAdminMenuIcon(group.Icon),
      items: visibleItems
        .filter((item) => item.ParentId === group.AdminMenuItemId)
        .sort((a, b) => a.SortOrder - b.SortOrder)
        .map(toNavItem),
    }))
    .filter((group) => group.items.length > 0);

  const rootLinks = topLevel.filter((item) => item.ItemType !== "Group").map(toNavItem);

  const activeGroupKey = menuGroups.find((group) => group.items.some((item) => item.active))?.key ?? null;
  const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(activeGroupKey);

  // 每次切換頁面（或選單資料載入完成），側邊選單只自動展開目前頁面所屬的主選單，其餘主選單預設收合。
  useEffect(() => {
    setExpandedGroupKey(activeGroupKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, menuItems]);

  return (
    <main className="portal-rwd min-h-screen bg-[#f6f7f5] text-[#24352d]">
      {mobileMenuOpen && (
        <button
          aria-label="關閉側邊選單"
          className="fixed inset-0 z-30 bg-[#10251c]/35 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
          type="button"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 w-[300px] border-r border-[#dfe6e1] bg-white transition-[transform,width] duration-200 lg:translate-x-0 ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        } ${collapsed ? "lg:w-[76px]" : "lg:w-[320px]"}`}
      >
        <div className="flex h-full flex-col">
          <div
            className={`flex h-[86px] items-center border-b border-[#edf0ee] px-4 ${
              sidebarCollapsed ? "justify-center" : "justify-between gap-2"
            }`}
          >
            <BrandLogo collapsed={sidebarCollapsed} />

            <button
              aria-label="關閉側邊選單"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2] lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
              type="button"
            >
              <X size={18} />
            </button>

            {!sidebarCollapsed && (
              <button
                aria-label="收合側邊選單"
                className="hidden h-9 w-9 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2] lg:inline-flex"
                onClick={() => setCollapsed(true)}
                type="button"
              >
                <PanelLeftClose size={18} />
              </button>
            )}
          </div>

          <nav className={`flex-1 space-y-2 px-3 py-4 ${sidebarCollapsed ? "overflow-visible" : "overflow-y-auto"}`}>
            <a
              className={`flex items-center gap-2 rounded-md px-3 text-sm font-bold transition ${
                currentPath === "/admin/dashboard" ? "bg-[#e9f4ec] text-[#245438]" : "text-[#59665f] hover:bg-[#f0f5f2] hover:text-[#315746]"
              } ${sidebarCollapsed ? "h-12 w-full justify-center" : "h-10"}`}
              href="/admin/dashboard"
              onClick={() => setMobileMenuOpen(false)}
              title="資訊統整表"
            >
              <LayoutDashboard className="h-[18px] w-[18px] shrink-0 text-[#315746]" size={18} />
              {!sidebarCollapsed && "資訊統整表"}
            </a>
            {!sidebarCollapsed &&
              rootLinks.map((item) => <NavLink item={item} key={item.key} onNavigate={() => setMobileMenuOpen(false)} />)}
            {menuGroups.map((group) => (
              <AdminMenuGroup
                collapsed={sidebarCollapsed}
                expanded={expandedGroupKey === group.key}
                groupLabel={group.label}
                icon={group.icon}
                items={group.items}
                key={group.key}
                onNavigate={() => setMobileMenuOpen(false)}
                onToggle={() => setExpandedGroupKey((current) => (current === group.key ? null : group.key))}
              />
            ))}
          </nav>

          <div className="border-t border-[#edf0ee] p-3">
            <button
              className={`inline-flex h-10 items-center rounded-md border border-[#cfd8d2] bg-white font-bold text-[#315746] transition hover:bg-[#f0f5f2] ${
                sidebarCollapsed ? "w-full justify-center px-0" : "w-full justify-center gap-2 px-3"
              }`}
              onClick={onLogout}
              title="登出"
              type="button"
            >
              <LogOut size={18} />
              {!sidebarCollapsed && "登出"}
            </button>
          </div>
        </div>
      </aside>

      <div className={`min-h-screen transition-[padding] duration-200 ${desktopContentPadding}`}>
        <header className="sticky top-0 z-20 border-b border-[#dfe6e1] bg-white/95 backdrop-blur">
          <div className="flex min-h-[56px] items-center justify-between gap-4 px-4 py-2.5 md:min-h-[64px] md:px-6 md:py-3">
            <div className="flex min-w-0 items-center gap-3">
              <button
                aria-label={collapsed ? "展開側邊選單" : "收合側邊選單"}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] bg-white text-[#315746] transition hover:bg-[#f0f5f2]"
                onClick={() => {
                  if (window.matchMedia("(max-width: 1023px)").matches) {
                    setMobileMenuOpen(true);
                    return;
                  }
                  setCollapsed((current) => !current);
                }}
                type="button"
              >
                {collapsed ? <PanelLeftOpen size={18} /> : <Menu size={18} />}
              </button>

              <AdminBreadcrumb
                currentTag="h1"
                items={[
                  { label: "總管理後台", href: "/admin/dashboard", icon: <Home size={13} /> },
                  { label: sectionLabel },
                  { label: title },
                ]}
              />
            </div>

            <div className="hidden shrink-0 items-center gap-2 text-sm text-[#59665f] sm:flex">
              <ShieldCheck size={18} />
              <span>{user?.DisplayName || "系統管理員"}</span>
            </div>
          </div>
        </header>

        <section className="w-full max-w-none px-3 py-4 sm:px-4 md:px-5 md:py-5 2xl:px-6">{children}</section>
      </div>
    </main>
  );
}
